/**
 * Force-directed graph on a 2D canvas (scope §7.2): d3-force layout, pan/zoom,
 * quadtree culling, label LOD above a zoom threshold, colour by type, size by
 * degree, and a pulse hook for nodes touched by an open case (Phase 6).
 *
 * Layout caching: positions seed the simulation; only nodes without a cached
 * position are relaxed with a short simulation, then the layout is persisted.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { forceSimulation, forceManyBody, forceLink, forceCenter, forceCollide, forceX, forceY, type Simulation, type SimulationNodeDatum, type SimulationLinkDatum } from 'd3-force';
import { zoom as d3zoom, zoomIdentity, type ZoomTransform } from 'd3-zoom';
import { select } from 'd3-selection';
import type { GraphPayload } from '@cortex/shared';
import { useTypes } from '../../hooks/useVault';

export interface GraphCanvasProps {
  data: GraphPayload;
  /** Cluster key per node id (entity view): nodes with the same key are pulled together. */
  clusters?: Record<string, string>;
  positions?: Record<string, [number, number]>;
  onPositions?: (positions: Record<string, [number, number]>) => void;
  onOpen: (id: string, split: boolean) => void;
  highlightId?: string | null;
  /** Node ids to pulse (open cases). */
  pulse?: Set<string>;
  height?: number | string;
}

interface N extends SimulationNodeDatum {
  id: string;
  title: string;
  type: string;
  degree: number;
  r: number;
  colour: string;
  fixed: boolean;
}
interface L extends SimulationLinkDatum<N> {
  kind: string;
}

const FALLBACK = '#8890aa';

export function GraphCanvas({ data, clusters, positions, onPositions, onOpen, highlightId, pulse, height = '100%' }: GraphCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const simRef = useRef<Simulation<N, L> | null>(null);
  const nodesRef = useRef<N[]>([]);
  const linksRef = useRef<L[]>([]);
  const transformRef = useRef<ZoomTransform>(zoomIdentity);
  const fitRef = useRef<() => void>(() => undefined);
  const hoverRef = useRef<N | null>(null);
  const [hover, setHover] = useState<N | null>(null);
  const types = useTypes();
  const colourOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of types.data ?? []) m.set(t.key, t.colour ?? FALLBACK);
    return (type: string) => m.get(type) ?? FALLBACK;
  }, [types.data]);
  const reduced = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

  // Build simulation whenever data changes.
  useEffect(() => {
    const maxDeg = Math.max(1, ...data.nodes.map((n) => n.degree));
    const nodes: N[] = data.nodes.map((n) => {
      const p = positions?.[n.id];
      return {
        id: n.id,
        title: n.title,
        type: n.type,
        degree: n.degree,
        r: 3 + 9 * Math.sqrt(n.degree / maxDeg),
        colour: colourOf(n.type),
        fixed: !!p,
        x: p ? p[0] : undefined,
        y: p ? p[1] : undefined,
      };
    });
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const links: L[] = data.edges.filter((e) => e.to && byId.has(e.from) && byId.has(e.to)).map((e) => ({ source: e.from, target: e.to!, kind: e.kind }));
    // New nodes start near a neighbour that has a position, so incremental relaxation is short.
    for (const n of nodes) {
      if (n.x !== undefined) continue;
      const nb = links.find((l) => (l.source === n.id && byId.get(l.target as string)?.x !== undefined) || (l.target === n.id && byId.get(l.source as string)?.x !== undefined));
      const anchor = nb ? byId.get((nb.source === n.id ? nb.target : nb.source) as string) : undefined;
      n.x = (anchor?.x ?? 0) + (Math.random() - 0.5) * 60;
      n.y = (anchor?.y ?? 0) + (Math.random() - 0.5) * 60;
    }
    const allCached = nodes.length > 0 && nodes.every((n) => n.fixed);
    nodesRef.current = nodes;
    linksRef.current = links;

    simRef.current?.stop();
    const sim = forceSimulation<N>(nodes)
      .force('charge', forceManyBody<N>().strength((d) => -30 - d.degree * 4).distanceMax(600))
      .force('link', forceLink<N, L>(links).id((d) => d.id).distance((l) => 40 + 10 * Math.min(4, ((l.source as N).degree + (l.target as N).degree) / 8)).strength(0.4))
      .force('collide', forceCollide<N>().radius((d) => d.r + 3).iterations(1))
      .force('center', forceCenter(0, 0).strength(0.02));
    if (clusters) {
      // Pull each cluster to its own centroid on a ring so sites read as groups.
      const keys = [...new Set(Object.values(clusters))];
      const centre = new Map(keys.map((k, i) => [k, [Math.cos((i / keys.length) * Math.PI * 2) * 420, Math.sin((i / keys.length) * Math.PI * 2) * 420] as const]));
      sim.force('cx', forceX<N>((d) => centre.get(clusters[d.id] ?? '')?.[0] ?? 0).strength((d) => (clusters[d.id] ? 0.08 : 0)));
      sim.force('cy', forceY<N>((d) => centre.get(clusters[d.id] ?? '')?.[1] ?? 0).strength((d) => (clusters[d.id] ? 0.08 : 0)));
    }
    // Cached positions: hold them still; only relax the newcomers for a short while.
    for (const n of nodes) if (n.fixed) { n.fx = n.x; n.fy = n.y; }
    sim.alpha(allCached ? 0 : nodes.some((n) => n.fixed) ? 0.4 : 1).alphaDecay(nodes.length > 3000 ? 0.05 : 0.0228);
    sim.on('tick', () => draw());
    sim.on('end', () => {
      for (const n of nodes) { n.fx = null; n.fy = null; }
      fitRef.current();
      draw();
      if (onPositions && nodes.length) onPositions(Object.fromEntries(nodes.map((n) => [n.id, [n.x ?? 0, n.y ?? 0] as [number, number]])));
    });
    simRef.current = sim;
    if (allCached) { draw(); setTimeout(() => fitRef.current(), 50); }
    return () => { sim.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, clusters, colourOf]);

  // Zoom / pan / hover / click.
  useEffect(() => {
    const canvas = canvasRef.current!;
    const sel = select(canvas);
    const z = d3zoom<HTMLCanvasElement, unknown>()
      .scaleExtent([0.05, 8])
      .on('zoom', (ev) => { transformRef.current = ev.transform; draw(); });
    sel.call(z);
    // Fit on first layout.
    const fit = () => {
      const ns = nodesRef.current;
      if (!ns.length) return;
      const xs = ns.map((n) => n.x ?? 0), ys = ns.map((n) => n.y ?? 0);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
      const w = canvas.width / devicePixelRatio, h = canvas.height / devicePixelRatio;
      const k = Math.min(3, 0.9 * Math.min(w / Math.max(1, maxX - minX + 80), h / Math.max(1, maxY - minY + 80)));
      sel.call(z.transform, zoomIdentity.translate(w / 2, h / 2).scale(k).translate(-(minX + maxX) / 2, -(minY + maxY) / 2));
    };
    fitRef.current = fit;
    const t = setTimeout(fit, 400);
    const move = (e: MouseEvent) => {
      const n = pick(e);
      if (n !== hoverRef.current) { hoverRef.current = n; setHover(n); draw(); }
      canvas.style.cursor = n ? 'pointer' : 'grab';
    };
    const click = (e: MouseEvent) => {
      const n = pick(e);
      if (n) onOpen(n.id, e.ctrlKey || e.metaKey || e.shiftKey);
    };
    const dbl = () => fit();
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('click', click);
    canvas.addEventListener('dblclick', dbl);
    return () => { clearTimeout(t); canvas.removeEventListener('mousemove', move); canvas.removeEventListener('click', click); canvas.removeEventListener('dblclick', dbl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Dev hook so the 45 fps @ 10k target can be measured honestly (full redraw cost, not event plumbing).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __cortexGraph?: unknown }).__cortexGraph = {
      draw: () => draw(),
      fit: () => fitRef.current(),
      nodes: () => nodesRef.current.length,
      visible: () => {
        const canvas = canvasRef.current!;
        const t = transformRef.current;
        const w = canvas.width / devicePixelRatio, h = canvas.height / devicePixelRatio;
        const x0 = -t.x / t.k, y0 = -t.y / t.k, x1 = (w - t.x) / t.k, y1 = (h - t.y) / t.k;
        return nodesRef.current.filter((n) => (n.x ?? 0) >= x0 && (n.x ?? 0) <= x1 && (n.y ?? 0) >= y0 && (n.y ?? 0) <= y1).length;
      },
    };
    return () => { delete (window as unknown as { __cortexGraph?: unknown }).__cortexGraph; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize with the container (DPR-aware).
  useEffect(() => {
    const wrap = wrapRef.current!, canvas = canvasRef.current!;
    const ro = new ResizeObserver(() => {
      const { width, height } = wrap.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(width * devicePixelRatio));
      canvas.height = Math.max(1, Math.floor(height * devicePixelRatio));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      draw();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pulse animation for active-case nodes (skipped under reduced motion).
  useEffect(() => {
    if (!pulse?.size || reduced) return;
    let raf = 0;
    const loop = () => { draw(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse, reduced]);

  function pick(e: MouseEvent): N | null {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const t = transformRef.current;
    const x = (e.clientX - rect.left - t.x) / t.k, y = (e.clientY - rect.top - t.y) / t.k;
    let best: N | null = null, bd = Infinity;
    for (const n of nodesRef.current) {
      const dx = (n.x ?? 0) - x, dy = (n.y ?? 0) - y;
      const d = dx * dx + dy * dy;
      const rr = (n.r + 4 / t.k) ** 2;
      if (d < rr && d < bd) { best = n; bd = d; }
    }
    return best;
  }

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const t = transformRef.current;
    const w = canvas.width / devicePixelRatio, h = canvas.height / devicePixelRatio;
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.translate(t.x, t.y);
    ctx.scale(t.k, t.k);
    // Visible world rect for culling.
    const x0 = -t.x / t.k - 40, y0 = -t.y / t.k - 40, x1 = (w - t.x) / t.k + 40, y1 = (h - t.y) / t.k + 40;
    const vis = (n: N) => (n.x ?? 0) >= x0 && (n.x ?? 0) <= x1 && (n.y ?? 0) >= y0 && (n.y ?? 0) <= y1;
    const hov = hoverRef.current;
    const neighbours = new Set<string>();
    if (hov) for (const l of linksRef.current) { const s = l.source as N, tg = l.target as N; if (s.id === hov.id) neighbours.add(tg.id); if (tg.id === hov.id) neighbours.add(s.id); }

    // Edges: thin strokes; brighten those touching the hovered node.
    ctx.lineWidth = 1 / t.k;
    for (const l of linksRef.current) {
      const s = l.source as N, tg = l.target as N;
      if (!vis(s) && !vis(tg)) continue;
      const active = hov && (s.id === hov.id || tg.id === hov.id);
      ctx.strokeStyle = active ? 'rgba(91,156,248,0.9)' : l.kind === 'TYPED' ? 'rgba(91,156,248,0.28)' : 'rgba(136,144,170,0.22)';
      ctx.beginPath();
      ctx.moveTo(s.x ?? 0, s.y ?? 0);
      ctx.lineTo(tg.x ?? 0, tg.y ?? 0);
      ctx.stroke();
    }
    // Nodes.
    const now = performance.now();
    for (const n of nodesRef.current) {
      if (!vis(n)) continue;
      const dim = hov && n.id !== hov.id && !neighbours.has(n.id);
      if (pulse?.has(n.id) && !reduced) {
        const ph = (now / 1200) % 1;
        ctx.beginPath();
        ctx.arc(n.x ?? 0, n.y ?? 0, n.r + 4 + ph * 14, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(245,158,11,${(1 - ph) * 0.6})`;
        ctx.lineWidth = 2 / t.k;
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(n.x ?? 0, n.y ?? 0, n.r, 0, Math.PI * 2);
      ctx.fillStyle = dim ? 'rgba(78,84,112,0.6)' : n.colour;
      ctx.fill();
      if (n.id === highlightId || n.id === hov?.id) {
        ctx.lineWidth = 2 / t.k;
        ctx.strokeStyle = '#eceef5';
        ctx.stroke();
      }
    }
    // Labels. While hovering, only the hovered node is labelled (its neighbours stay bright but
    // unlabelled: a vendor hub has a hundred of them and the labels were unreadable). Otherwise
    // labels appear above a zoom threshold, for well-connected nodes, and for the open note.
    const showLabels = t.k > 1.1;
    ctx.font = `${Math.max(9, 11 / t.k)}px Inter, system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#eceef5';
    for (const n of nodesRef.current) {
      if (!vis(n)) continue;
      const important = hov
        ? n.id === hov.id
        : n.id === highlightId || (showLabels && (n.degree >= 3 || t.k > 2)) || (nodesRef.current.length < 150 && t.k > 0.6);
      if (!important) continue;
      ctx.fillText(n.title, (n.x ?? 0) + n.r + 3 / t.k, n.y ?? 0);
    }
  }

  return (
    <div ref={wrapRef} className="graph-wrap" style={{ height }}>
      <canvas ref={canvasRef} />
      {hover && (
        <div className="graph-tip mono">
          <span className="type-dot" style={{ background: hover.colour }} /> {hover.title} <span className="faint">· {hover.type} · {hover.degree} links</span>
        </div>
      )}
    </div>
  );
}
